import Axios from 'axios';
import { SERVER_URL } from 'Constants';

const PARKING_API_URL = `${SERVER_URL}/api/parking`;

// DTOs
export interface ParkingLocationDto {
	id: string;
	name: string;
	address?: string;
	disabled?: boolean;
	maxCapacity?: number;
	instructions?: string;
	parkingTermsAndConditions?: string;
	locationId?: string;
	linkedLocationName?: string;
	hasCapacityRestrictions?: boolean;
	hasActivePricing?: boolean;
}

export interface ParkingAvailabilityRequest {
	locationId: string;
	startDate: string;
	endDate: string;
}

export interface ParkingCapacitySlot {
	parkingVehicleTypeId: string;
	parkingVehicleTypeName: string;
	max: number;
	occupied: number;
	available: number;
	overbookingDisabled: boolean;
}

export interface ParkingDayAvailability {
	date: string;
	slots: ParkingCapacitySlot[];
	locationMaxCapacity?: number;
	locationOccupied?: number;
	locationAvailable?: number;
}

export interface ParkingAvailabilityResponse {
	locationId: string;
	days: ParkingDayAvailability[];
}

export interface ParkingVehicleQuoteRequest {
	parkingVehicleTypeId: string;
	startDate: string;
	endDate: string;
}

export interface ParkingPriceQuoteRequest {
	locationId: string;
	vehicles: ParkingVehicleQuoteRequest[];
}

export interface ParkingVehicleQuote {
	parkingVehicleTypeId: string;
	startDate: string;
	endDate: string;
	days: number;
	dailyRate: number;
	subTotal: number;
	pricingTierId?: string;
	pricingTierName?: string;
}

export interface ParkingPriceQuoteResponse {
	locationId: string;
	totalPrice: number;
	vehicles: ParkingVehicleQuote[];
}

export interface CreateParkingReservationVehicle {
	parkingVehicleTypeId: string;
	startDate: string;
	endDate: string;
	registration?: string;
	make?: string;
	model?: string;
}

export interface CreateParkingReservationRequest {
	locationId: string;
	transactionId?: string;
	note?: string;
	userId?: string;
	inCheckIn?: boolean;
	vehicles: CreateParkingReservationVehicle[];
}

export interface CreateParkingReservationResult {
	reservationId: string;
	reservationIds: string[];
	transactionId: string;
	humanReadableId: string;
	humanReadableIds: string[];
	totalPrice: number;
}

export interface ModifyParkingReservationRequest {
	reservationId: string;
	note?: string;
	forceCreditRefund?: boolean;
	inCheckIn?: boolean;
	existingTransactionId?: string;
	vehicles: CreateParkingReservationVehicle[];
}

export interface ModifyParkingReservationResult {
	reservationId: string;
	humanReadableId: string;
	transactionId: string;
	previousPrice: number;
	newPrice: number;
	priceDelta: number;
	paymentRequired: boolean;
	refundMethod?: string;
	voucherCode?: string;
}

export interface ParkingReservationDto {
	id: string;
	humanReadableId: string;
	status: string;
	totalPrice: number;
	note?: string;
	userId: string;
	transactionId?: string;
	parkingLocationId: string;
	parkingLocation?: ParkingLocationDto;
	vehicles: ParkingReservationVehicleDto[];
	created: string;
}

export interface ParkingReservationVehicleDto {
	id: string;
	parkingVehicleTypeId: string;
	registration?: string;
	make?: string;
	model?: string;
	startDate: string;
	endDate: string;
	dailyRate: number;
	subTotal: number;
}

export interface ParkingVehicleTypeDto {
	id: string;
	name: string;
	description?: string;
	iconName?: string;
}

// API Functions
export async function getParkingLocations(): Promise<ParkingLocationDto[]> {
	const res = await Axios.get<ParkingLocationDto[]>(`${PARKING_API_URL}/locations`);
	return res.data;
}

export async function getParkingVehicleTypes(availableOnly = false): Promise<ParkingVehicleTypeDto[]> {
	const res = await Axios.get<ParkingVehicleTypeDto[]>(`${PARKING_API_URL}/vehicle-types`, {
		params: availableOnly ? { availableOnly: true } : undefined,
	});
	return res.data;
}

export async function getParkingAvailability(
	locationId: string,
	startDate: string,
	endDate: string,
	excludeReservationId?: string,
): Promise<ParkingAvailabilityResponse> {
	const params: Record<string, string> = { locationId, startDate, endDate };
	if (excludeReservationId) {
		params.excludeReservationId = excludeReservationId;
	}
	const res = await Axios.get<ParkingAvailabilityResponse>(`${PARKING_API_URL}/availability`, { params });
	return res.data;
}

export async function getParkingPriceQuote(request: ParkingPriceQuoteRequest): Promise<ParkingPriceQuoteResponse> {
	const res = await Axios.post<ParkingPriceQuoteResponse>(`${PARKING_API_URL}/price-quote`, request);
	return res.data;
}

export async function createParkingReservation(
	request: CreateParkingReservationRequest,
): Promise<CreateParkingReservationResult> {
	const res = await Axios.post<CreateParkingReservationResult>(`${PARKING_API_URL}/reserve`, request);
	return res.data;
}

export async function modifyParkingReservation(
	id: string,
	request: ModifyParkingReservationRequest,
): Promise<ModifyParkingReservationResult> {
	const res = await Axios.put<ModifyParkingReservationResult>(`${PARKING_API_URL}/reserve/${id}`, request);
	return res.data;
}

export interface ParkingCancellationResult {
	refundAmount: number;
	refundMethod: string | null;
	voucherCode: string | null;
}

export async function cancelParkingReservation(
	id: string,
	forceCreditRefund = false,
	cancellationReasonId?: string,
	cancellationReasonDetail?: string,
): Promise<ParkingCancellationResult> {
	const res = await Axios.post<ParkingCancellationResult>(`${PARKING_API_URL}/reserve/${id}/cancel`, {
		forceCreditRefund,
		cancellationReasonId,
		cancellationReasonDetail,
	});
	return res.data;
}

export async function getParkingReservations(): Promise<ParkingReservationDto[]> {
	const res = await Axios.get<ParkingReservationDto[]>(`${PARKING_API_URL}/reservations`);
	return res.data;
}

export interface ParkingReservationDetailDto {
	id: string;
	humanReadableId: string;
	status: string;
	totalPrice: number;
	note?: string;
	checkedIn: boolean;
	created: string;
	userId: string;
	userName?: string;
	userEmail?: string;
	userPhone?: string;
	locationId?: string;
	locationName?: string;
	locationAddress?: string;
	locationInstructions?: string;
	vehicles: {
		id: string;
		parkingVehicleTypeId: string;
		parkingVehicleTypeName?: string;
		parkingVehicleTypeIconName?: string;
		registration?: string;
		make?: string;
		model?: string;
		startDate: string;
		endDate: string;
		dailyRate: number;
		subTotal: number;
		pricingTierName?: string;
	}[];
}

export async function getParkingReservationById(id: string): Promise<ParkingReservationDetailDto> {
	const res = await Axios.get<ParkingReservationDetailDto>(`${PARKING_API_URL}/reservations/${id}`);
	return res.data;
}

// Parking reservation summary for booking success / cart cards
export interface ParkingReservationSummaryVehicleDto {
	id: string;
	parkingVehicleTypeId: string;
	parkingVehicleTypeName?: string;
	parkingVehicleTypeIconName?: string;
	registration?: string;
	startDate: string;
	endDate: string;
	dailyRate: number;
	subTotal: number;
}

export interface ParkingReservationSummaryDto {
	id: string;
	humanReadableId: string;
	status: string;
	totalPrice: number;
	parkingLocationId: string;
	locationName?: string;
	vehicles: ParkingReservationSummaryVehicleDto[];
}

export async function getParkingReservationsByTransactionId(
	transactionId: string,
): Promise<ParkingReservationSummaryDto[]> {
	const res = await Axios.get<ParkingReservationSummaryDto[]>(
		`${PARKING_API_URL}/reservations/by-transaction/${transactionId}`,
	);
	return res.data;
}

export async function deleteParkingReservationsByTransactionId(transactionId: string): Promise<void> {
	await Axios.delete(`${PARKING_API_URL}/reservations/by-transaction/${transactionId}`);
}

// Staff filtered reservations
export interface ParkingReservationListVehicleDto {
	id: string;
	startDate: string;
	endDate: string;
	dailyRate: number;
	subTotal: number;
	rego?: string;
	make?: string;
	model?: string;
	parkingVehicleTypeId?: string;
	parkingVehicleTypeName?: string;
}

export interface ParkingReservationListDto {
	id: string;
	humanReadableId: string;
	status: string;
	totalPrice: number;
	note?: string;
	checkedIn: boolean;
	hasNotes: boolean;
	created: string;
	userId: string;
	userName?: string;
	userEmail?: string;
	userPhone?: string;
	parkingLocationId: string;
	locationName?: string;
	transactionId?: string;
	transactionHumanReadableId?: string;
	vehicleCount: number;
	/**
	 * Optional because the API can omit it. Treat it as possibly absent at every read - the check-in
	 * parking page took the whole app to the error boundary when one row came back without it.
	 */
	vehicles?: ParkingReservationListVehicleDto[];
}

export interface ParkingReservationFilterOptions {
	searchString?: string;
	userId?: string;
	startDate?: Date;
	endDate?: Date;
	dateFilterType?: string;
	statusFilters?: string[];
	locationFilters?: string[];
	orderBy?: string;
	orderByDescending?: boolean;
	skip?: number;
	take?: number;
	tripDate?: string;
	/** Restrict to reservations with a vehicle parked on `tripDate` (inclusive of both ends). */
	onTripDate?: boolean;
}

export async function fetchFilteredParkingReservations(
	options: ParkingReservationFilterOptions,
): Promise<{ data: ParkingReservationListDto[]; count: number }> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}

	if (options.userId) {
		params.append('userId', options.userId);
	}

	if (options.statusFilters && options.statusFilters.length > 0) {
		for (const status of options.statusFilters) {
			params.append('statusFilters', status);
		}
	}

	if (options.locationFilters && options.locationFilters.length > 0) {
		for (const location of options.locationFilters) {
			params.append('locationFilters', location);
		}
	}

	if (options.startDate) {
		params.append('startDate', options.startDate.toISOString());
	}

	if (options.endDate) {
		params.append('endDate', options.endDate.toISOString());
	}

	if (options.dateFilterType) {
		params.append('dateFilterType', options.dateFilterType);
	}

	if (options.orderBy) {
		params.append('orderBy', options.orderBy);
	}

	if (options.orderByDescending !== undefined) {
		params.append('orderByDescending', String(options.orderByDescending));
	}

	if (options.skip !== undefined) {
		params.append('skip', String(options.skip));
	}

	if (options.take !== undefined) {
		params.append('take', String(options.take));
	}

	if (options.tripDate) {
		params.append('tripDate', options.tripDate);
	}

	if (options.onTripDate) {
		params.append('onTripDate', 'true');
	}

	const response = await Axios.get(`${PARKING_API_URL}/reservations/filtered`, { params });

	return {
		data: response.data.data as ParkingReservationListDto[],
		count: response.data.count,
	};
}

/**
 * Far above what one car park holds on one day. The endpoint pages at 10 when no `take` is sent, and
 * that is what hid reservations from check-in (#2720) — so the trip-date list asks for all of them.
 */
const TRIP_DATE_PARKING_TAKE = 1000;

/**
 * Every active or upcoming parking reservation with a vehicle parked on the ferry trip's date — the
 * check-in parking list, its count badge and the rego scanner all read this, under one query key.
 */
export function fetchParkingReservationsForTripDate(
	tripDate: string,
): Promise<{ data: ParkingReservationListDto[]; count: number }> {
	return fetchFilteredParkingReservations({
		statusFilters: ['Active', 'Confirmed'],
		tripDate,
		onTripDate: true,
		take: TRIP_DATE_PARKING_TAKE,
	});
}

/**
 * `ferryTripId` is the trip whose check-in this is done from. A reservation has no trip of its own, so it
 * is the only way the check-in reaches that trip's history; leave it out anywhere else.
 */
export async function checkInParkingReservation(
	id: string,
	checkedIn: boolean,
	ferryTripId?: string,
): Promise<{ checkedIn: boolean }> {
	const res = await Axios.post<{ checkedIn: boolean }>(`${PARKING_API_URL}/reservations/${id}/check-in`, {
		checkedIn,
		ferryTripId: ferryTripId || undefined,
	});
	return res.data;
}

export interface ParkingNoteInfoDto {
	noteId: string;
	userName: string;
	creationDateTime: string;
	content: string;
	noteType: string;
	locationType: string;
	// Written with nobody signed in (a background task) — see noteInfoDto.createdBySystem.
	createdBySystem?: boolean;
}

export async function getParkingReservationNotes(id: string): Promise<ParkingNoteInfoDto[]> {
	const res = await Axios.get<ParkingNoteInfoDto[]>(`${PARKING_API_URL}/reservations/${id}/notes`);
	return res.data;
}

export async function addParkingReservationNote(id: string, content: string): Promise<void> {
	await Axios.post(`${PARKING_API_URL}/reservations/${id}/notes`, { content });
}
